/**
 * A campaign's holdout readout — slice 61. The view only; `CampaignHoldoutCard`
 * fetches. Split so `verify-sense-screens.tsx` can render every state: the
 * fetching module imports the API client, which evaluates only inside Vite.
 *
 * ## THE TITLE CLAIMS NOTHING; THE STATE SAYS WHERE IT IS
 *
 * "Sent vs held back", with a chip for the state — Waiting, Not measured, Too
 * few to measure — because a title like "What this campaign changed" claimed an
 * effect over a card that, most days, has none to show (review). Only
 * "measured" has rates, and only measured leads with a figure.
 *
 * ## THE SENTENCE CARRIES THE UNCERTAINTY, NOT A CHIP AT THE END OF IT
 *
 * A result that could be chance is written as one — "about 84 more conversions
 * — could be chance" — not "84 conversions the campaign added" with a chip a
 * reader's eye never reaches. Conversions and rupees each take their own
 * direction from their own sign, as magnitudes with a word ("fewer", "less"),
 * never "−₹18,000 the campaign cost", which reads as a gain. The rupee figure is
 * always labelled an estimate: one large loan in the held-back group moves it
 * by more than the conversions do.
 *
 * The difference is in percentage POINTS (33% against 10% is +23 points), never
 * a relative "+230%", which flatters small numbers and is the figure a reader
 * will quote.
 *
 * ## ONE PANEL PER GROUP — SLICE 66
 *
 * The two groups are the Blueprint's `MetricPanel`s side by side (its P16
 * comparison): the people who converted as each panel's figure, what they are
 * a share of in the sub-line ("120 converted", then "of 360 sent · 33.3%"), and a meter of
 * that rate. Sent wears the brand wash and Held back is plain, so the eye finds
 * the group the campaign went to first; the meters are both brand and on the
 * same 0–100% scale, so their lengths compare honestly. The meter is magnitude
 * only — the verdict is the lead sentence above the panels, which still says
 * "could be chance" whenever it could be, and still speaks in percentage
 * points. The lead is a sans figure now, not mono: the dotted zero misreads.
 */
import { Link } from 'react-router-dom';

import { HOLDOUT_MIN_PEOPLE, HOLDOUT_WINDOW_DAYS, OUTCOME_TIER_LABEL, type HoldoutReadout } from '@wend/shared';
import { Badge, Card, CardContent, CardHeader, CardTitle, MetricPanel, StateChip } from '@wend/ui';

import { formatCalendarDay, formatCount, formatRupees } from '../../lib/format';

const PERCENT = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 });
const ONE_DECIMAL = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
const SHORT = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
const shortDay = (day: string): string => SHORT.format(new Date(`${day}T00:00:00Z`));

/** "+23.3 percentage points" / "−5 percentage points" / "no difference". */
function points(sentRate: number, heldRate: number): string {
  const value = Math.round((sentRate - heldRate) * 1000) / 10;
  if (value === 0) return 'no difference in the rate';
  return `${value > 0 ? '+' : '−'}${ONE_DECIMAL.format(Math.abs(value))} percentage points`;
}

export function HoldoutReadoutView({ readout }: { readout: HoldoutReadout }) {
  if (readout.state === 'off') return null;

  const window = readout.window;
  const meta = [
    `${formatCount(readout.held.people)} held back (${String(readout.percent)}%)`,
    `${formatCount(readout.sent.people)} sent`,
    ...(window === null ? [] : [`counted ${shortDay(window.from)} – ${shortDay(window.to)}`]),
  ].join(' · ');

  return (
    <Card data-role="holdout-readout" data-state={readout.state}>
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="flex flex-wrap items-center gap-2 text-[0.95rem]">
          Sent vs held back
          <Badge tone="neutral">{OUTCOME_TIER_LABEL.matched}</Badge>
          {readout.state === 'waiting' && <StateChip state="awaiting" label="Waiting" />}
          {readout.state === 'not_measured' && <StateChip state="not_measured" />}
          {readout.state === 'too_few' && <StateChip state="under_floor" label="Too few to measure" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-[18px] pt-0 text-[0.9rem]">
        {/* Sans tabular, not mono: this line is three counts and a share, and
            mono's dotted zero reads as an 8 (R6; slice 66 review). */}
        <p className="text-[0.8rem] tabular-nums text-muted" data-role="holdout-meta">
          {meta}
        </p>

        {readout.state === 'waiting' && (
          <p className="text-muted">
            {readout.readyOn === null
              ? 'Readout once the campaign has finished sending.'
              : `Readout after ${formatCalendarDay(readout.readyOn)} — ${String(HOLDOUT_WINDOW_DAYS)} days after the last send — once a list of who converted is uploaded.`}
          </p>
        )}

        {readout.state === 'not_measured' && (
          <p className="text-muted">
            {readout.reason}
            {readout.fixedByUpload && (
              <>
                {' '}
                <Link to="/sense/outcomes" className="text-text underline underline-offset-4">
                  Upload on Outcomes
                </Link>
              </>
            )}
          </p>
        )}

        {readout.state === 'too_few' && (
          <p className="text-muted">
            {formatCount(Math.min(readout.held.people, readout.sent.people))}{' '}
            {readout.held.people < readout.sent.people ? 'held back' : 'sent'}, and a comparison needs at least{' '}
            {String(HOLDOUT_MIN_PEOPLE)} in each group.
          </p>
        )}

        {readout.state === 'measured' && <Measured readout={readout} />}
      </CardContent>
    </Card>
  );
}

function Measured({ readout }: { readout: Extract<HoldoutReadout, { state: 'measured' }> }) {
  const { result } = readout;
  const window = readout.window;

  if (result.sentConverted + result.heldConverted === 0) {
    return (
      <p className="text-muted" data-role="holdout-nobody">
        Nobody in either group converted between {shortDay(window.from)} and {shortDay(window.to)}.
      </p>
    );
  }

  const conversions = Math.round(result.incrementalConversions);
  const value = result.incrementalValueMinor;
  const lead =
    conversions === 0
      ? 'No clear difference'
      : `about ${formatCount(Math.abs(conversions))} ${conversions > 0 ? 'more' : 'fewer'} conversion${Math.abs(conversions) === 1 ? '' : 's'}`;
  const money = value === 0 ? null : `${formatRupees(Math.abs(value))} ${value > 0 ? 'more' : 'less'}`;

  return (
    <>
      <div className="flex flex-col gap-1" data-role="holdout-lift">
        <p className="text-[1.35rem] font-semibold leading-tight tracking-[-0.03em] text-text">
          {lead.charAt(0).toUpperCase() + lead.slice(1)}
          {conversions !== 0 && (result.significant ? ' because of this campaign' : ' — could be chance')}
        </p>
        <p className="text-[0.82rem] text-muted">
          {points(result.sentRate, result.heldRate)}
          {money !== null && ` · ${money} in conversions (an estimate)`}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <MetricPanel
          data-group="sent"
          kicker="Sent"
          figureSize="md"
          figure={{ kind: 'measured', value: result.sentConverted, unit: 'converted' }}
          sub={`of ${formatCount(readout.sent.people)} sent · ${PERCENT.format(result.sentRate)}`}
          meter={{ value: result.sentRate }}
        />
        <MetricPanel
          data-group="held"
          variant="plain"
          kicker="Held back"
          figureSize="md"
          figure={{ kind: 'measured', value: result.heldConverted, unit: 'converted' }}
          sub={`of ${formatCount(readout.held.people)} held back · ${PERCENT.format(result.heldRate)}`}
          meter={{ value: result.heldRate }}
        />
      </div>
    </>
  );
}
